import { apiRequest, apiRoutes } from '../../api';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Tooltip, useMediaQuery } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChatIcon from '@mui/icons-material/Chat';
import '@fontsource/vazirmatn';

import Header from '../../shared/layout/Header';
import MapContainer from './MapContainer';
import Chatbot from './Chatbot';
import ListingDtl from './ListingDtl';
import FilterPanel, { buildActiveChips, CHIP_CLEAR, matchStreetWidthBucket } from './FilterPanel';
import MessagesPanel from './MessagesPanel';
import AiSearchSummary from './AiSearchSummary';
import {
    aiMatchFromResponse,
    filtersFromAiMatch,
    rankListingsByAiMatch,
    selectionContains,
} from './aiSearchSync';
import './mainPage.css';

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
    listingType: 'all',
    propertyType: 'all',
    usageType: 'all',
    minArea: 0,
    maxArea: 0,
    geoFilters: [],   // [{ type: 'city'|'region'|'neighborhood', id, name }, ...]
    maxPrice: 0,
    idealDeposit: 0,
    idealRent: 0,
    totalBudget: 0,
    depositBudgetMax: 0,
    rentBudgetMax: 0,
    amenities: {},
    geoFeatures: {},
    aiCandidateHexIds: [],
    baseMap: 'minimal',
    overlayLayers: [],
};

function filtersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const number = (key) => Number(params.get(key)) || 0;
    const selection = (key) => {
        const rawValue = params.get(key);
        if (!rawValue?.trim()) return 'all';

        const values = rawValue
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
            .map(Number)
            .filter(value => Number.isFinite(value) && value > 0);
        return values.length === 0 ? 'all' : values.length === 1 ? values[0] : values;
    };
    return {
        ...DEFAULT_FILTERS,
        listingType: params.get('intent') || 'all',
        propertyType: selection('property_type'),
        usageType: selection('usage'),
        minArea: number('min_area'),
        maxArea: number('max_area'),
        maxPrice: number('max_price'),
        totalBudget: number('total_budget'),
        depositBudgetMax: number('max_deposit'),
        rentBudgetMax: number('max_rent'),
    };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Count the number of non-default filter values that are active. */
function countActiveFilters(filters) {
    let count = 0;
    if (filters.listingType !== 'all') count++;
    if (filters.propertyType !== 'all') count++;
    if (filters.usageType !== 'all') count++;
    if (filters.minArea > 0) count++;
    if (filters.maxArea > 0) count++;
    if (filters.geoFilters && filters.geoFilters.length > 0) count++;
    if (filters.maxPrice > 0) count++;
    if (filters.totalBudget > 0 || filters.depositBudgetMax > 0 || filters.rentBudgetMax > 0) count++;
    const amenityCount = Object.keys(filters.amenities || {}).length;
    if (amenityCount > 0) count += amenityCount;
    const geoCount = Object.keys(filters.geoFeatures || {}).length;
    if (geoCount > 0) count += geoCount;
    if (filters.aiCandidateHexIds?.length > 0) count++;
    return count;
}

// ── Component ─────────────────────────────────────────────────────────────────

function MainPage() {
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const preAiFiltersRef = useRef(null);
    // The split-pane workspace becomes cramped well before phone widths.
    // Treat tablets as compact devices too and use the mutually-exclusive
    // bottom sheets rather than allowing map controls and sidebars to collide.
    const isMobile = useMediaQuery('(max-width: 1024px)');

    // State management
    const [properties, setProperties] = useState([]);
    const [visibleProperties, setVisibleProperties] = useState([]);
    const [aiMatch, setAiMatch] = useState(null);

    // Whenever the full listings list changes, recompute visible ones from current map bounds.
    useEffect(() => {
        if (!properties.length) {
            setVisibleProperties([]);
            return;
        }
        if (aiMatch?.active) {
            setVisibleProperties(rankListingsByAiMatch(properties, aiMatch));
            return;
        }
        if (mapRef.current) {
            try {
                const map = mapRef.current.getMap();
                const bounds = map.getBounds();
                const visible = properties.filter(listing => {
                    const loc = listing.property?.location;
                    if (!loc || !loc.coordinates) return false;
                    const [lon, lat] = loc.coordinates;
                    return bounds.contains([lon, lat]);
                });
                // Only fall back to all if map not yet initialised (bounds returns null)
                setVisibleProperties(visible);
            } catch {
                setVisibleProperties(properties);
            }
        } else {
            setVisibleProperties(properties);
        }
    }, [properties, aiMatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const [error, setError] = useState(null);
    const [retryKey, setRetryKey] = useState(0);

    // ── Right sidebar: houses both Chatbot and MessagesPanel ─────────────────
    const [showRightSidebar, setShowRightSidebar] = useState(false);
    const [rightTab, setRightTab] = useState('messages'); // 'messages' | 'chatbot'
    const [leftTab, setLeftTab] = useState('filters'); // 'filters' | 'results'
    const [mobileSheet, setMobileSheet] = useState(null); // filters | results | communication

    useEffect(() => {
        if (!mobileSheet) return undefined;
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setMobileSheet(null);
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [mobileSheet]);

    const [selectedProperty, setSelectedProperty] = useState(null);
    const [amenitiesData, setAmenitiesData] = useState([]);

    // Lookup tables from FilterPanel — used to label chips when panel is collapsed
    const [filterLookups, setFilterLookups] = useState({
        propertyTypes: [], usageTypes: [], geoFeatures: [],
    });

    const handleLookupsChange = (partial) => {
        setFilterLookups(prev => ({ ...prev, ...partial }));
    };

    // Sort priorities from the SortingListings component
    // [{ featureId, rank, featureDef }]  — rank 1 = highest importance
    const [sortPriorities, setSortPriorities] = useState([]);

    // Map state
    const [viewState, setViewState] = useState({
        longitude: 51.4262,
        latitude: 35.7643,
        zoom: 11
    });

    // Filter states
    const [filters, setFilters] = useState(filtersFromUrl);

    // Keep the core search shareable and browser-navigation friendly.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            const params = new URLSearchParams(window.location.search);
            const put = (key, value, empty = 0) => {
                if (value && value !== empty && value !== 'all') params.set(key, Array.isArray(value) ? value.join(',') : value);
                else params.delete(key);
            };
            put('intent', filters.listingType, 'all');
            put('property_type', filters.propertyType, 'all');
            put('usage', filters.usageType, 'all');
            put('min_area', filters.minArea);
            put('max_area', filters.maxArea);
            put('max_price', filters.maxPrice);
            put('total_budget', filters.totalBudget);
            put('max_deposit', filters.depositBudgetMax);
            put('max_rent', filters.rentBudgetMax);
            const query = params.toString();
            window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [filters.listingType, filters.propertyType, filters.usageType, filters.minArea, filters.maxArea, filters.maxPrice, filters.totalBudget, filters.depositBudgetMax, filters.rentBudgetMax]);

    // ③ Active filter count for the badge
    const activeFilterCount = countActiveFilters(filters);

    // Chips for the collapsed-panel strip
    const activeChips = buildActiveChips(
        filters,
        filterLookups.propertyTypes,
        filterLookups.usageTypes,
        amenitiesData,
    );

    const handleRemoveChip = (chipKey) => {
        const resets = CHIP_CLEAR[chipKey] || {};
        Object.entries(resets).forEach(([k, v]) => handleFilterChange(k, v));
    };

    const [showFilters, setShowFilters] = useState(true);
    const [showLayerControl, setShowLayerControl] = useState(false);
    const [activeLayers, setActiveLayers] = useState([]);
    const [drawnArea, setDrawnArea] = useState(null);

    // Drawing state
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawingPoints, setDrawingPoints] = useState([]);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [mapKey, setMapKey] = useState(0);

    // Distance search state
    const [distanceSearchState, setDistanceSearchState] = useState({
        isActive: false,
        center: null,
        radius: 2
    });
    const [distanceBuffer, setDistanceBuffer] = useState(null);

    // Messages panel state
    const [unreadCount, setUnreadCount] = useState(0);

    // Poll unread count every 30s while panel is closed
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const fetchUnread = () => {
            apiRequest(apiRoutes.dealbridge.chatrooms, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((r) => r.json())
                .then((data) => {
                    const total = Array.isArray(data)
                        ? data.reduce((s, r) => s + (r.unread_count || 0), 0)
                        : 0;
                    setUnreadCount(total);
                })
                .catch(() => { });
        };
        fetchUnread();
        const id = setInterval(fetchUnread, 30000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/login');
            return;
        }
    }, [navigate]);

    const handleFilterChange = (name, value) => {
        // Once the user manually changes a synchronized filter, return control
        // to the regular filter engine instead of retaining a stale AI ID set.
        setAiMatch(null);
        preAiFiltersRef.current = null;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            aiCandidateHexIds: name === 'aiCandidateHexIds' ? value : [],
        }));
    };

    const handleChatbotResponse = (payload) => {
        const match = payload?.action?.match;
        if (!match) return;

        const nextAiMatch = aiMatchFromResponse(payload);
        if (!aiMatch?.active) preAiFiltersRef.current = filters;
        setFilters(prev => filtersFromAiMatch(prev, match, {
            amenities: amenitiesData,
            geoFeatures: filterLookups.geoFeatures,
        }));
        setAiMatch(nextAiMatch);
        setSelectedProperty(null);
        if (nextAiMatch) {
            setLeftTab('results');
            if (isMobile) setMobileSheet('results');
            else setShowFilters(true);
        }
    };

    // The chatbot may answer before FilterPanel has loaded its amenity/HCM
    // lookup tables. Re-apply only when those lookup tables become available.
    useEffect(() => {
        if (!aiMatch?.criteria) return;
        setFilters(prev => filtersFromAiMatch(prev, aiMatch.criteria, {
            amenities: amenitiesData,
            geoFeatures: filterLookups.geoFeatures,
        }));
    }, [amenitiesData, filterLookups.geoFeatures, aiMatch?.criteria]);

    const handleGeoFilterChange = (item) => {
        // item: { type, id, name } — toggles in/out of the geoFilters array
        setAiMatch(null);
        setFilters(prev => {
            const exists = prev.geoFilters.some(f => f.type === item.type && f.id === item.id);
            const geoFilters = exists
                ? prev.geoFilters.filter(f => !(f.type === item.type && f.id === item.id))
                : [...prev.geoFilters, item];
            return { ...prev, geoFilters, aiCandidateHexIds: [] };
        });
    };

    // Resets all filters back to defaults (preserves baseMap choice)
    const handleClearFilters = () => {
        setAiMatch(null);
        preAiFiltersRef.current = null;
        setFilters(prev => ({ ...DEFAULT_FILTERS, baseMap: prev.baseMap }));
    };

    const handleUndoAiSearch = () => {
        setAiMatch(null);
        if (preAiFiltersRef.current) setFilters(preAiFiltersRef.current);
        preAiFiltersRef.current = null;
    };

    const openFilters = () => {
        setLeftTab('filters');
        if (isMobile) setMobileSheet('filters');
        else setShowFilters(true);
    };

    const openResults = () => {
        setLeftTab('results');
        if (isMobile) setMobileSheet('results');
        else setShowFilters(true);
    };

    const openCommunication = (tab = 'chatbot') => {
        setRightTab(tab);
        if (isMobile) setMobileSheet('communication');
        else setShowRightSidebar(true);
    };

    const handleDistanceSearchStateChange = (newState) => {
        setDistanceSearchState(newState);
    };

    const handleDistanceBufferChange = (newBuffer) => {
        setDistanceBuffer(newBuffer);
    };

    const isPointInPolygon = (point, polygon) => {
        const [x, y] = point;
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];
            if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
                inside = !inside;
            }
        }
        return inside;
    };

    const filterProperties = (listings, amenitiesDataParam = amenitiesData) => {
        const candidates = rankListingsByAiMatch(listings, aiMatch);

        // OpenSearch has already applied the complete AI contract (including
        // HCM bucket values and exact hex filters). The synchronized panel is
        // a readable representation of that query; applying its simpler UI
        // rules again can incorrectly remove valid OpenSearch results.
        if (aiMatch?.active) return candidates;

        return candidates.filter(listing => {
            const prop = listing.property || {};

            if (filters.listingType !== 'all') {
                const typeMap = { rent: 'RENT', sale: 'SALE', auction: 'AUCTION', presale: 'PRESALE' };
                if (listing.listing_type !== typeMap[filters.listingType]) return false;
            }

            if (prop.area != null) {
                if (filters.minArea > 0 && prop.area < filters.minArea) return false;
                if (filters.maxArea > 0 && prop.area > filters.maxArea) return false;
            }

            if (filters.propertyType !== 'all' && !selectionContains(filters.propertyType, prop.property_type_id)) return false;
            if (filters.usageType !== 'all' && !selectionContains(filters.usageType, prop.usage_id)) return false;

            if (filters.geoFilters && filters.geoFilters.length > 0) {
                // Group selected filters by type
                const cityIds = filters.geoFilters.filter(f => f.type === 'city').map(f => f.id);
                const regionIds = filters.geoFilters.filter(f => f.type === 'region').map(f => f.id);
                const neighborhoodIds = filters.geoFilters.filter(f => f.type === 'neighborhood').map(f => f.id);
                // Property must match at least one selected item per type that has selections
                if (cityIds.length > 0 && !cityIds.includes(prop.city_id)) return false;
                if (regionIds.length > 0 && !regionIds.includes(prop.region_id)) return false;
                if (neighborhoodIds.length > 0 && !neighborhoodIds.includes(prop.neighborhood_id)) return false;
            }

            if (listing.listing_type === 'RENT') {
                // listing.price = total_cost, listing.deposit = min_deposit, listing.min_rent = min_rent
                const totalCost = parseFloat(listing.price) || 0;
                const minDeposit = parseFloat(listing.deposit) || 0;
                const minRent = parseFloat(listing.min_rent) || 0;

                if (filters.totalBudget > 0 && totalCost > filters.totalBudget) return false;
                if (filters.depositBudgetMax > 0 && minDeposit > filters.depositBudgetMax) return false;
                if (filters.rentBudgetMax > 0 && minRent > filters.rentBudgetMax) return false;
            }
            if (listing.listing_type === 'SALE' || listing.listing_type === 'AUCTION' || listing.listing_type === 'PRESALE') {
                const price = parseFloat(listing.price) || 0;
                if (filters.maxPrice > 0 && price > filters.maxPrice) return false;
            }

            if (filters.amenities && Object.keys(filters.amenities).length > 0) {
                const propAmenities = prop.amenities || [];
                for (const [amenityId, filterValue] of Object.entries(filters.amenities)) {
                    const pa = propAmenities.find(a => a.id === parseInt(amenityId));
                    if (!pa) return false;
                    const amenityDef = amenitiesDataParam.find(a => a.id === parseInt(amenityId));
                    if (amenityDef) {
                        if (amenityDef.value_type === 'boolean' && filterValue && !pa.value) return false;
                        if (amenityDef.value_type === 'numerical') {
                            const [min, max] = filterValue;
                            if (pa.value < min || pa.value > max) return false;
                        }
                        if (amenityDef.value_type === 'categorical') {
                            // filterValue is an array of selected keys (OR logic)
                            const selected = Array.isArray(filterValue) ? filterValue : [filterValue];
                            if (selected.length > 0 && !selected.map(Number).includes(Number(pa.value))) return false;
                        }
                        if (amenityDef.value_type === 'text' && (!pa.value || !pa.value.toLowerCase().includes(filterValue.toLowerCase()))) return false;
                    }
                }
            }

            if (filters.geoFeatures && Object.keys(filters.geoFeatures).length > 0) {
                const propGeoFeatures = prop.geo_features || [];
                const geoFeatureDefs = filterLookups.geoFeatures || [];
                for (const [featureId, filterValue] of Object.entries(filters.geoFeatures)) {
                    const gf = propGeoFeatures.find(f => f.id === parseInt(featureId));
                    const featureDef = geoFeatureDefs.find(f => f.id === parseInt(featureId));
                    const ft = featureDef?.feature_type;

                    // boolean: property must have truthy value
                    if (ft === 'boolian') {
                        if (filterValue && (!gf || !gf.value)) return false;
                        continue;
                    }

                    // categorical: filterValue is an array of selected options (OR logic)
                    if (ft === 'categorical') {
                        const selected = Array.isArray(filterValue) ? filterValue : [];
                        if (selected.length === 0) continue;
                        if (!gf) return false;

                        // street_width: match against buckets
                        if (featureDef?.name === 'street_width') {
                            const matches = selected.some(bucketId => matchStreetWidthBucket(gf.value, bucketId));
                            if (!matches) return false;
                        } else {
                            // zonning / zone_desc: string match
                            const propVal = String(gf.value ?? '');
                            if (!selected.includes(propVal)) return false;
                        }
                        continue;
                    }

                    // range slider types: filterValue is [min, max]
                    if (!gf || gf.value < filterValue[0] || gf.value > filterValue[1]) return false;
                }
            }

            const loc = prop.location;
            if (drawnArea && loc && loc.coordinates) {
                const [lon, lat] = loc.coordinates;
                if (!isPointInPolygon([lon, lat], drawnArea)) return false;
            }

            if (distanceBuffer && loc && loc.coordinates) {
                const [lon, lat] = loc.coordinates;
                if (!isPointInPolygon([lon, lat], distanceBuffer.polygon)) return false;
            }

            return true;
        });
    };

    const displayedCount = filterProperties(visibleProperties).length;

    const filterPane = (
        <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onAmenitiesDataChange={setAmenitiesData}
            onClearFilters={handleClearFilters}
            onLookupsChange={handleLookupsChange}
            sortPriorities={sortPriorities}
            onSortPrioritiesChange={setSortPriorities}
        />
    );

    const resultsPane = (
        <div className="results-pane">
            <AiSearchSummary
                aiMatch={aiMatch}
                chips={activeChips}
                onEdit={openFilters}
                onContinue={() => openCommunication('chatbot')}
                onUndo={handleUndoAiSearch}
            />
            <ListingDtl
                visibleProperties={visibleProperties}
                filterProperties={filterProperties}
                setViewState={setViewState}
                setSelectedProperty={setSelectedProperty}
                selectedProperty={selectedProperty}
                setProperties={setProperties}
                setError={setError}
                retryKey={retryKey}
                filters={filters}
                onClearFilters={handleClearFilters}
                sortPriorities={sortPriorities}
                aiMatch={aiMatch}
            />
        </div>
    );

    const communicationPane = (
        <div className="right-sidebar">
            <div className="rs-tabs" role="tablist" aria-label="ارتباطات">
                <button
                    id="messages-tab"
                    role="tab"
                    aria-controls="messages-panel"
                    className={`rs-tab ${rightTab === 'messages' ? 'active' : ''}`}
                    onClick={() => setRightTab('messages')}
                    aria-selected={rightTab === 'messages'}
                >
                    <span>💬</span><span>پیام‌ها</span>
                    {unreadCount > 0 && <span className="rs-tab-badge">{unreadCount}</span>}
                </button>
                <button
                    id="assistant-tab"
                    role="tab"
                    aria-controls="assistant-panel"
                    className={`rs-tab ${rightTab === 'chatbot' ? 'active' : ''}`}
                    onClick={() => setRightTab('chatbot')}
                    aria-selected={rightTab === 'chatbot'}
                >
                    <ChatIcon fontSize="small" /><span>دستیار</span>
                </button>
            </div>
            <div className="rs-content">
                <div id="messages-panel" role="tabpanel" aria-labelledby="messages-tab" className={`rs-pane ${rightTab === 'messages' ? 'visible' : 'hidden'}`}>
                    <MessagesPanel inline onUnreadChange={setUnreadCount} />
                </div>
                <div id="assistant-panel" role="tabpanel" aria-labelledby="assistant-tab" className={`rs-pane ${rightTab === 'chatbot' ? 'visible' : 'hidden'}`}>
                    <Chatbot onResponse={handleChatbotResponse} onSessionReset={handleClearFilters} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="property-finder-container">
            <Header
                onMessagesClick={() => {
                    openCommunication('messages');
                    setUnreadCount(0);
                }}
                unreadCount={unreadCount}
            />

            {/* ── Main workspace ── */}
            <div className={`main-workspace ${isMobile ? 'mobile-workspace' : ''}`}>
                <PanelGroup direction="horizontal" style={{ height: '100%' }}>

                    {!isMobile && showFilters && (
                        <Panel defaultSize={26} minSize={22} maxSize={36}>
                            <aside className="search-rail" aria-label="جستجو و نتایج">
                                <div className="rail-tabs" role="tablist" aria-label="جستجوی ملک">
                                    <button role="tab" aria-selected={leftTab === 'filters'} className={leftTab === 'filters' ? 'active' : ''} onClick={() => setLeftTab('filters')}>
                                        فیلترها {activeFilterCount > 0 && <span>{activeFilterCount.toLocaleString('fa-IR')}</span>}
                                    </button>
                                    <button role="tab" aria-selected={leftTab === 'results'} className={leftTab === 'results' ? 'active' : ''} onClick={() => setLeftTab('results')}>
                                        نتایج <span>{displayedCount.toLocaleString('fa-IR')}</span>
                                    </button>
                                    <button type="button" className="rail-close" onClick={() => setShowFilters(false)} aria-label="بستن پنل جستجو">×</button>
                                </div>
                                <div className={`rail-pane ${leftTab === 'filters' ? 'active' : ''}`} aria-hidden={leftTab !== 'filters'}>{filterPane}</div>
                                <div className={`rail-pane ${leftTab === 'results' ? 'active' : ''}`} aria-hidden={leftTab !== 'results'}>{resultsPane}</div>
                            </aside>
                        </Panel>
                    )}

                    {!isMobile && showFilters && (
                        <PanelResizeHandle className="resize-handle-vertical">
                            <div className="resize-grip"><span /><span /><span /></div>
                        </PanelResizeHandle>
                    )}

                    {/* ── Centre: Map ── */}
                    <Panel minSize={40}>
                        <div className="map-zone">

                            {!isMobile && <div className="filter-toggle-wrap">
                                <Tooltip title={showFilters ? 'پنهان کردن فیلترها' : 'نمایش فیلترها'} placement="bottom">
                                    <button
                                        className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                                        onClick={() => showFilters ? setShowFilters(false) : openFilters()}
                                        aria-label="فیلترها"
                                    >
                                        <FilterListIcon fontSize="small" />
                                        {activeFilterCount > 0 && (
                                            <span className="filter-badge">{activeFilterCount}</span>
                                        )}
                                    </button>
                                </Tooltip>

                                {!showFilters && activeChips.length > 0 && (
                                    <div className="collapsed-filter-chips">
                                        {activeChips.map((chip) => (
                                            <span key={chip.key} className="collapsed-chip">
                                                {chip.label}
                                                <button
                                                    className="collapsed-chip-remove"
                                                    onClick={() => handleRemoveChip(chip.key)}
                                                    aria-label={`حذف ${chip.label}`}
                                                >×</button>
                                            </span>
                                        ))}
                                        <button className="collapsed-chip-clear" onClick={handleClearFilters}>
                                            پاک کردن همه
                                        </button>
                                    </div>
                                )}
                            </div>}

                            <MapContainer
                                viewState={viewState}
                                setViewState={setViewState}
                                visibleProperties={visibleProperties}
                                selectedProperty={selectedProperty}
                                setSelectedProperty={setSelectedProperty}
                                filters={filters}
                                setFilters={setFilters}
                                isDrawing={isDrawing}
                                setIsDrawing={setIsDrawing}
                                drawingPoints={drawingPoints}
                                setDrawingPoints={setDrawingPoints}
                                isMouseDown={isMouseDown}
                                setIsMouseDown={setIsMouseDown}
                                drawnArea={drawnArea}
                                setDrawnArea={setDrawnArea}
                                distanceSearchState={distanceSearchState}
                                distanceBuffer={distanceBuffer}
                                onDistanceSearchStateChange={handleDistanceSearchStateChange}
                                onDistanceBufferChange={handleDistanceBufferChange}
                                filterProperties={filterProperties}
                                mapKey={mapKey}
                                activeLayers={activeLayers}
                                mapRef={mapRef}
                                properties={properties}
                                setVisibleProperties={setVisibleProperties}
                                setMapKey={setMapKey}
                                setActiveLayers={setActiveLayers}
                                showLayerControl={showLayerControl}
                                setShowLayerControl={setShowLayerControl}
                                onGeoFilterChange={handleGeoFilterChange}
                                sortPriorities={sortPriorities}
                                aiMatch={aiMatch}
                            />

                            {error && (
                                <div className="workspace-error" role="alert">
                                    <span>{error}</span>
                                    <button type="button" onClick={() => { setError(null); setRetryKey(value => value + 1); }}>تلاش دوباره</button>
                                    <button type="button" className="icon" onClick={() => setError(null)} aria-label="بستن خطا">×</button>
                                </div>
                            )}

                            {!isMobile && <div className="right-sidebar-toggle-wrap">
                                <Tooltip title={showRightSidebar ? 'بستن پنل' : 'پیام‌ها و دستیار'} placement="left">
                                    <button
                                        className={`right-sidebar-toggle-btn ${showRightSidebar ? 'active' : ''}`}
                                        onClick={() => {
                                            if (!showRightSidebar) { setUnreadCount(0); }
                                            showRightSidebar ? setShowRightSidebar(false) : openCommunication('chatbot');
                                        }}
                                        aria-label="پیام‌ها و دستیار"
                                    >
                                        <ChatIcon fontSize="small" />
                                        {!showRightSidebar && unreadCount > 0 && (
                                            <span className="filter-badge">{unreadCount}</span>
                                        )}
                                    </button>
                                </Tooltip>
                            </div>}

                            {!isMobile && <button
                                className={`listing-tab ${showFilters && leftTab === 'results' ? 'open' : ''}`}
                                onClick={openResults}
                                aria-expanded={showFilters && leftTab === 'results'}
                            >
                                <span className="listing-tab-chevron">▲</span>
                                <span className="listing-tab-label">
                                    {displayedCount > 0
                                        ? `${displayedCount} ملک در این محدوده`
                                        : 'نتیجه‌ای یافت نشد'}
                                </span>
                                {displayedCount > 0 && (
                                    <span className="listing-tab-badge">{displayedCount}</span>
                                )}
                            </button>}
                        </div>
                    </Panel>

                    {!isMobile && showRightSidebar && (
                        <PanelResizeHandle className="resize-handle-vertical">
                            <div className="resize-grip"><span /><span /><span /></div>
                        </PanelResizeHandle>
                    )}

                    {!isMobile && showRightSidebar && (
                        <Panel defaultSize={22} minSize={18} maxSize={35}>
                            {communicationPane}
                        </Panel>
                    )}
                </PanelGroup>

                {isMobile && (
                    <>
                        {mobileSheet && <button type="button" className="mobile-sheet-backdrop" onClick={() => setMobileSheet(null)} aria-label="بستن پنل" />}
                        <nav className="mobile-action-bar" aria-label="ابزارهای جستجو">
                            <button type="button" className={mobileSheet === 'filters' ? 'active' : ''} onClick={() => setMobileSheet(mobileSheet === 'filters' ? null : 'filters')}>
                                <FilterListIcon fontSize="small" /><span>فیلترها</span>{activeFilterCount > 0 && <b>{activeFilterCount.toLocaleString('fa-IR')}</b>}
                            </button>
                            <button type="button" className={mobileSheet === 'results' ? 'active' : ''} onClick={() => setMobileSheet(mobileSheet === 'results' ? null : 'results')}>
                                <span aria-hidden="true">⌂</span><span>نتایج</span><b>{displayedCount.toLocaleString('fa-IR')}</b>
                            </button>
                            <button type="button" className={mobileSheet === 'communication' ? 'active' : ''} onClick={() => setMobileSheet(mobileSheet === 'communication' ? null : 'communication')}>
                                <ChatIcon fontSize="small" /><span>دستیار</span>{unreadCount > 0 && <b>{unreadCount.toLocaleString('fa-IR')}</b>}
                            </button>
                        </nav>

                        <section className={`mobile-sheet filters ${mobileSheet === 'filters' ? 'open' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mobile-filters-title" aria-hidden={mobileSheet !== 'filters'}>
                            <div className="mobile-sheet-handle" /><h2 className="mobile-sheet-title" id="mobile-filters-title">فیلترهای جستجو</h2><button className="mobile-sheet-close" onClick={() => setMobileSheet(null)} aria-label="بستن فیلترها">×</button>
                            {filterPane}
                        </section>
                        <section className={`mobile-sheet results ${mobileSheet === 'results' ? 'open' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mobile-results-title" aria-hidden={mobileSheet !== 'results'}>
                            <div className="mobile-sheet-handle" /><h2 className="mobile-sheet-title" id="mobile-results-title">{displayedCount.toLocaleString('fa-IR')} نتیجه در این محدوده</h2><button className="mobile-sheet-close" onClick={() => setMobileSheet(null)} aria-label="بستن نتایج">×</button>
                            {resultsPane}
                        </section>
                        <section className={`mobile-sheet communication ${mobileSheet === 'communication' ? 'open' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mobile-communication-title" aria-hidden={mobileSheet !== 'communication'}>
                            <div className="mobile-sheet-handle" /><h2 className="mobile-sheet-title" id="mobile-communication-title">پیام‌ها و دستیار هوشمند</h2><button className="mobile-sheet-close" onClick={() => setMobileSheet(null)} aria-label="بستن ارتباطات">×</button>
                            {communicationPane}
                        </section>
                    </>
                )}
            </div>

        </div>
    );
}

export default MainPage;
